import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { useTelegram } from '@/shared/hooks/useTelegram';
import { cn } from '@/shared/lib/cn';

import { Spinner } from '../Spinner/Spinner';

import styles from './Button.module.css';

export type ButtonVariant = 'gold' | 'teal' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

const SPINNER_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'gold',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    icon,
    type = 'button',
    className,
    onClick,
    children,
    ...rest
  },
  ref,
) {
  const { haptic } = useTelegram();
  const isBlocked = disabled || loading;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={isBlocked}
      aria-busy={loading || undefined}
      className={cn(
        styles.button,
        styles[variant],
        styles[size],
        fullWidth && styles.fullWidth,
        loading && styles.loading,
        className,
      )}
      onClick={(event) => {
        if (isBlocked) return;
        haptic(variant === 'danger' ? 'medium' : 'light');
        onClick?.(event);
      }}
    >
      {loading ? (
        <span className={styles.spinner}>
          <Spinner size={SPINNER_SIZE[size]} />
        </span>
      ) : null}
      {icon !== undefined && !loading ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className={styles.label}>{children}</span>
    </button>
  );
});
